import { useEffect } from 'react'
import { Alert, App, Form, InputNumber, Modal, Select, Switch, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { CHANNELS, createSubscription, fetchTemplates, updateSubscription, type SubscriptionView } from './notifyApi'
import { NOTIFY_EVENTS, eventName, templateName } from './notifyLabels'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface SubscriptionFormModalProps {
  open: boolean
  /** `null` = yangi obuna */
  subscription: SubscriptionView | null
  onClose: () => void
}

interface FormValues {
  eventType: string
  channel: (typeof CHANNELS)[number]
  templateCode: string
  priority: number
  enabled: boolean
}

/**
 * Obuna — "qaysi hodisa qaysi kanal(lar) orqali, qanday ustuvorlik bilan
 * xabar beradi" (`SubscriptionController` javadoc'i).
 *
 * ⚠️ `eventType`/`channel`/`templateCode` FAQAT yaratishda tahriranadi —
 * backend `PUT` faqat `priority`+`enabled` qabul qiladi (README kontrakt
 * jadvali). Boshqa uchligini o'zgartirish YANGI obuna: eski o'chirilib,
 * yangisi yaratiladi — bu ataylab shunday, chunki uchlik amalda obunaning
 * IDENTIFIKATORI.
 */
export function SubscriptionFormModal({
  open,
  subscription,
  onClose,
}: SubscriptionFormModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const isEdit = subscription !== null
  const channel = Form.useWatch('channel', form)

  const templates = useQuery({
    queryKey: queryKeys.notify.templates,
    queryFn: fetchTemplates,
    enabled: open && !isEdit,
  })

  const namedOption = (code: string, name: string | null) => ({
    value: code,
    label: name === null ? code : `${name} · ${code}`,
  })
  const eventCodes = subscription && !NOTIFY_EVENTS.some((e) => e === subscription.eventType)
    ? [...NOTIFY_EVENTS, subscription.eventType]
    : [...NOTIFY_EVENTS]
  const eventOptions = eventCodes.map((code) => namedOption(code, eventName(code, t)))
  const templateCodes = isEdit
    ? [subscription.templateCode]
    : [...new Set((templates.data ?? []).filter((tpl) => tpl.channel === channel).map((tpl) => tpl.code))].sort()
  const templateOptions = templateCodes.map((code) => namedOption(code, templateName(code, t)))

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      subscription
        ? {
            eventType: subscription.eventType,
            channel: subscription.channel,
            templateCode: subscription.templateCode,
            priority: subscription.priority,
            enabled: subscription.enabled,
          }
        : { channel: 'TELEGRAM', priority: 0, enabled: true },
    )
  }, [open, subscription, form])

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit
        ? updateSubscription(subscription.id, { priority: values.priority, enabled: values.enabled })
        : createSubscription({
            eventType: values.eventType,
            channel: values.channel,
            templateCode: values.templateCode,
            priority: values.priority,
            enabled: values.enabled,
          }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notify.subscriptions })
      // X-SIB-004 — muvaffaqiyatli amaldan keyin ANIQ tasdiq
      void message.success(isEdit ? t('common.toastSaved') : t('common.toastCreated'))
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={isEdit ? t('notify.subscription.editTitle') : t('notify.subscription.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={520}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Typography.Paragraph type="secondary">{t('notify.subscription.formHint')}</Typography.Paragraph>

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="eventType"
          label={t('notify.subscription.eventType')}
          extra={t('notify.subscription.eventTypeHint')}
          rules={[{ required: true, message: t('notify.subscription.eventTypeRequired') }]}
        >
          <Select
            autoFocus
            showSearch
            optionFilterProp="label"
            disabled={isEdit}
            options={eventOptions}
            placeholder={t('notify.subscription.eventPlaceholder')}
          />
        </Form.Item>

        <Form.Item name="channel" label={t('notify.channel')} rules={[{ required: true }]}>
          <Select
            disabled={isEdit}
            options={CHANNELS.map((c) => ({ value: c, label: t(`notify.channelName.${c}`) }))}
            onChange={() => form.setFieldsValue({ templateCode: undefined })}
          />
        </Form.Item>

        <Form.Item
          name="templateCode"
          label={t('notify.subscription.templateCode')}
          extra={t('notify.subscription.templateCodeHint')}
          rules={[{ required: true, message: t('notify.subscription.templateCodeRequired') }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            disabled={isEdit}
            loading={templates.isPending && !isEdit}
            options={templateOptions}
            placeholder={t('notify.subscription.templatePlaceholder')}
            notFoundContent={t('notify.subscription.noTemplateForChannel')}
          />
        </Form.Item>

        <Form.Item
          name="priority"
          label={t('notify.subscription.priority')}
          extra={t('notify.subscription.priorityHint')}
          rules={[{ required: true }]}
        >
          <InputNumber min={0} max={100} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="enabled" label={t('notify.subscription.enabled')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
